import React from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const HeroSection = ({ language }) => {
  const translations = {
    en: {
      headline: 'Transform Your Body, Transform Your Life',
      subheadline: 'Join the premier fitness center where your goals become reality. State-of-the-art equipment, expert trainers, and a community that supports your journey.',
      joinNow: 'Join Now',
      learnMore: 'Learn More',
      members: 'Active Members',
      trainers: 'Expert Trainers',
      years: 'Years Experience'
    },
    ar: {
      headline: 'حول جسمك، حول حياتك',
      subheadline: 'انضم إلى مركز اللياقة البدنية الرائد حيث تصبح أهدافك حقيقة. أحدث المعدات والمدربين الخبراء ومجتمع يدعم رحلتك.',
      joinNow: 'انضم الآن',
      learnMore: 'اعرف المزيد',
      members: 'عضو نشط',
      trainers: 'مدرب خبير',
      years: 'سنة خبرة'
    }
  };

  const t = translations[language];
  const isRTL = language === 'ar';

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className={`relative min-h-screen flex items-center ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <Image 
          src="https://images.pexels.com/photos/1552242/pexels-photo-1552242.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
          alt="Fitness Center"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60 sm:bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="text-white text-center lg:text-right">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6">
              {t.headline}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-6 sm:mb-8 leading-relaxed">
              {t.subheadline}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12 justify-center lg:justify-start">
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-green-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-base sm:text-lg flex items-center gap-2 justify-center"
              >
                <Icon name="Users" size={20} className="sm:w-6 sm:h-6" />
                {t.joinNow}
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-semibold text-base sm:text-lg flex items-center gap-2 justify-center"
              >
                <Icon name="Info" size={20} className="sm:w-6 sm:h-6" />
                {t.learnMore}
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center max-w-md mx-auto lg:mx-0">
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-green-400 mb-1 sm:mb-2">500+</div>
                <div className="text-xs sm:text-sm text-gray-300">{t.members}</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-green-400 mb-1 sm:mb-2">15</div>
                <div className="text-xs sm:text-sm text-gray-300">{t.trainers}</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-green-400 mb-1 sm:mb-2">10</div>
                <div className="text-xs sm:text-sm text-gray-300">{t.years}</div>
              </div>
            </div>
          </div>

          {/* Right Content - Features */}
          {/* Right Content - Features */}
<div className="hidden lg:block">
  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 xl:p-8 border border-white/20">
    <h3 className="text-xl xl:text-2xl font-bold text-white mb-4 xl:mb-6">
      {language === 'ar' ? 'لماذا تختار فيت برو؟' : 'Why Choose FitPro?'}
    </h3>
    <div className="space-y-3 xl:space-y-4">
      <div className="flex items-center gap-3 xl:gap-4 text-white">
        <div className="w-10 h-10 xl:w-12 xl:h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="Zap" size={20} className="xl:w-6 xl:h-6" color="white" />
        </div>
        <div>
          <div className="font-semibold text-sm xl:text-base">
            {language === 'ar' ? 'معدات حديثة' : 'Modern Equipment'}
          </div>
          <div className="text-gray-300 text-xs xl:text-sm">
            {language === 'ar' ? 'أحدث تقنيات اللياقة البدنية' : 'Latest fitness technology'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 xl:gap-4 text-white">
        <div className="w-10 h-10 xl:w-12 xl:h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="Users" size={20} className="xl:w-6 xl:h-6" color="white" />
        </div>
        <div>
          <div className="font-semibold text-sm xl:text-base">
            {language === 'ar' ? 'مدربون خبراء' : 'Expert Trainers'}
          </div>
          <div className="text-gray-300 text-xs xl:text-sm">
            {language === 'ar' ? 'محترفون معتمدون' : 'Certified professionals'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 xl:gap-4 text-white">
        <div className="w-10 h-10 xl:w-12 xl:h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="Clock" size={20} className="xl:w-6 xl:h-6" color="white" />
        </div>
        <div>
          <div className="font-semibold text-sm xl:text-base">
            {language === 'ar' ? 'دخول على مدار الساعة' : '24/7 Access'}
          </div>
          <div className="text-gray-300 text-xs xl:text-sm">
            {language === 'ar' ? 'تدرب في أي وقت تريده' : 'Train anytime you want'}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <button 
          onClick={() => scrollToSection('about')}
          className="animate-bounce text-white hover:text-green-400 transition-colors"
        >
          <Icon name="ChevronDown" size={28} className="sm:w-8 sm:h-8" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
